import { useEffect, useState } from 'react';

import FormTitle from 'components/PageLayout/FormTitle';
import CheckboxWrapper from 'components/curo-components/common/CheckboxWrapper/CheckboxWrapper';
import ButtonWithLoadingState from 'components/digifi-wrappers/ButtonWithLoadingState';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { BankAccountDataVariables, CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { TableValue } from 'product_modules/api/Types';
import media from 'styles/media.module.scss';
import { selectBankAccount } from 'thunks';
import styled from 'types/AppTheme';

const PageDescriptionWrapper = styled.div`
  max-width: 690px;
  margin-bottom: 36px;
`;

const GroupWrapper = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 36px;
  flex-wrap: wrap;

  & > label {
    flex-basis: 450px;

    @media (max-width: ${media.maxMobile}) {
      flex-grow: 1;
    }

    & > h5 {
      margin-bottom: 0;
    }
  }
`;

const PAGE_TITLE = 'Bank Account Information';
const PAGE_DESCRIPTION = 'Please select which account you would like to use to receive your funds.';

interface IBankAccountSelectStepProps {
  onContinueClick: () => void;
}

const BankAccountSelectStep = ({ onContinueClick }: IBankAccountSelectStepProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const trackEvent = useGoogleAnalytics();

  const bankAccountData = (
    application.variables[CuroApplicationVariables.BankAccountData] as TableValue
  ).filter((bankAccount) => bankAccount[BankAccountDataVariables.Displayed]);

  const initiallySelectedAccountId =
    bankAccountData.length === 1
      ? bankAccountData[0]._id
      : bankAccountData.find((account) => account[BankAccountDataVariables.Selected])?._id || null;

  const [selectedId, setSelectedId] = useState<string | null>(initiallySelectedAccountId);

  const dispatchWithUnwrap = useDispatchWithUnwrap();

  useEffect(() => {
    trackEvent(GoogleAnalyticsEventType.ApplyProcessBankAcctSelect);
  }, []);

  const handleContinueClick = async () => {
    if (selectedId) {
      await dispatchWithUnwrap(
        selectBankAccount({
          applicationId: application.id,
          selectedAccount: {
            selectedAccountId: selectedId,
          },
        }),
      );

      onContinueClick();
    }
  };

  const renderAccounts = () => {
    return bankAccountData.map((accountData) => {
      return (
        <CheckboxWrapper
          key={accountData._id}
          checked={selectedId === accountData._id}
          value={accountData._id}
          onChange={(value) => setSelectedId(value as string)}
          title={`${accountData[BankAccountDataVariables.AccountName]} ••••••${
            accountData[BankAccountDataVariables.AccountMask]
          }`}
        />
      );
    });
  };

  return (
    <>
      <FormTitle
        title={PAGE_TITLE}
        subTitle={<PageDescriptionWrapper>{PAGE_DESCRIPTION}</PageDescriptionWrapper>}
      />
      <GroupWrapper>{renderAccounts()}</GroupWrapper>
      <ButtonWithLoadingState
        type="submit"
        kind="primary"
        size="form"
        onClick={handleContinueClick}
        disabled={!selectedId}
      >
        Continue
      </ButtonWithLoadingState>
    </>
  );
};

export default BankAccountSelectStep;
